<!--
  @component
  Generates an HTML x-axis, useful for server-side rendered charts.  This component is also configured to detect if your x-scale is an ordinal scale. If so, it will place the markers in the middle of the bandwidth.
 -->
<script>
	import { getContext } from "svelte";
	import { fade, blur, fly, slide, scale } from "svelte/transition";
	import { animationParameters } from "../constants";
	import { tweened } from "svelte/motion";

	import { percent, px } from "../utils";

	const { xScale } = getContext("LayerCake");

	/** @type {Function} [formatTick=d => d] - A function that passes the current tick value and expects a nicely formatted value in return. */
	export let formatTick = (d, i, isFirst, isLast) => d;

	export let tickHeight = 6;
	export let tickOffsetY = -6;

	export let ticks = undefined;
	export let mobileTicks = undefined;
	export let monthLabel = undefined;

	/** @type {Number} [yTick=7] - The distance from the baseline to place each tick value, in pixels. */
	export let yTick = 12;

	$: isBandwidth = typeof $xScale.bandwidth === "function";

	$: tickVals = $xScale.domain().filter((t, i) => ticks(t, i));
	$: tickValsMobile = $xScale.domain().filter((t, i) => mobileTicks(t, i));

	$: lastTickLeft = $xScale(tickVals.at(-1)) + (isBandwidth ? $xScale.bandwidth() * 1 : 0);
	const tLastTickLeft = tweened(lastTickLeft, animationParameters);
	$: tLastTickLeft.set(lastTickLeft);
</script>

<div class="timeline">
	<div class="baseline" style:width="{$tLastTickLeft}%" />
	{#each tickVals as tick, i}
		{@const isFirst = i === 0}
		{@const isLast = i === tickVals.length - 1}
		<div
			class="tick-mark not-mobile"
			style:transform="translateX({percent(
				isLast
					? $tLastTickLeft
					: $xScale(tick) + (isBandwidth && !isFirst ? $xScale.bandwidth() * (isLast ? 1 : 0.5) : 0)
          )})"
      style:left="0"
			style:height="{tickHeight}px"
			style:top="calc(100% + {tickOffsetY}px)"
		/>
		<div
			class="tick not-mobile tick-{i}"
			class:isFirst
			class:isLast
			class:inMiddle={!isFirst && !isLast}
			style:transform="translateX({percent(
				isLast
					? $tLastTickLeft
					: $xScale(tick) + (isBandwidth && !isFirst ? $xScale.bandwidth() * (isLast ? 1 : 0.5) : 0)
			)})"
      style:left="0"
			style:top="calc(100% + {tickOffsetY}px)"
		>
			<div class="text" transition:fade={animationParameters} style:top={px(yTick)}>
				{#if monthLabel}
					<span class="month">{monthLabel}</span>
				{/if}
				<span class="year">{@html formatTick(tick, i, isFirst, isLast)}</span>
			</div>
		</div>
	{/each}
	{#each tickValsMobile as tick, i}
		{@const isFirst = i === 0}
		{@const isLast = i === tickValsMobile.length - 1}
		<div
			class="tick-mark mobile"
			style:transform="translateX({percent(
				isLast
					? $tLastTickLeft
					: $xScale(tick) + (isBandwidth && !isFirst ? $xScale.bandwidth() * (isLast ? 1 : 0.5) : 0)
          )})"
      style:left="0"
			style:height="{tickHeight}px"
			style:top="calc(100% + {tickOffsetY}px)"
		/>
		<div
			class="tick mobile tick-{i}"
			class:isFirst
			class:isLast
			class:inMiddle={!isFirst && !isLast}
			style:transform="translateX({percent(
				isLast
					? $tLastTickLeft
					: $xScale(tick) + (isBandwidth && !isFirst ? $xScale.bandwidth() * (isLast ? 1 : 0.5) : 0)
			)})"
      style:left="0"
			style:top="calc(100% + {tickOffsetY}px)"
		>
			<div class="text" transition:fade={animationParameters} style:top={px(yTick)}>
				{#if monthLabel}
					<span class="month">{monthLabel}</span>
				{/if}
				<span class="year">{@html formatTick(tick, i, isFirst, isLast)}</span>
			</div>
		</div>
	{/each}
</div>

<style lang="scss">
	@import "../main.scss";

	.timeline,
	.tick,
	.tick-mark,
	.baseline {
		position: absolute;
	}
	.timeline {
		width: 100%;
    height: 100%;
    pointer-events: none;
	}

	.tick-mark {
    border-left: 1px solid $grey;
    width: 100%;
  }

  .tick {
    width: 100%;
    text-align: center;
  }

	.baseline {
		border-top: 1px solid $grey;
		// width: 100%;
		top: 100%;
	}

	.tick .text {
		color: $grey;
		position: relative;
		white-space: nowrap;
		transform: translateX(-50%);
	}

	// <= small tablets
	@media (max-width: $breakpoint-tablet-s) {
		.tick:last-child {
      left: 100% !important;
      width: 100px;
      transform: translateX(-100%) !important;
       text-align: right;

       .text {
        transform: none;
       }
		}

		.timeline {
			top: $offset-timeline-mobile;
		}
	}

	// > small tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) {
		.month {
			display: none;
		}

		.timeline {
			top: $offset-timeline;
		}
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
		.inMiddle,
		.isLast {
			.month {
				display: none;
			}
		}
	}

	// > normal phones
	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.mobile {
			display: none;
		}
	}

	// <= normal phones
	@media (max-width: $breakpoint-mobile-l) {
		.not-mobile {
			display: none;
		}
	}
</style>
